// frontend/shared/src/ref.ts —— 推广码（代理专属链接 ?ref= / Mini App start_param）跨页面、跨会话留存（2026-10-21 归属修复）
//
// 用户硬性要求：经代理专属链接进入、注册时未手填邀请码，也必须归属该代理。官网（goodpayu.com）与 H5（app.goodpayu.com）
// 不同源，localStorage 各自隔离，两端各存一份（同键名、同格式）；官网 CTA 仍以 URL ?ref= 把码带给 H5。
// 规则：URL 新码（白名单合法）覆盖旧码并重置 30 天有效期；URL 无码时读回未过期的存储值；过期 / 损坏 / 非法即删除并视同无码。
// 只存推广码本身（非敏感、本就出现在公开链接里），不存任何账户信息；存储不可用（隐私模式 / SecurityError）时退化为仅当次 URL 生效。

export const REF_KEY = 'gpu_ref';
export const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;
// 与官网 refOf 同一白名单：查询串里的码须命中才进外链 / 请求体（XSS / 开放重定向面）；具体格式由后端 referralCodeFormatRe 终裁
export const REF_RE = /^[A-Za-z0-9_-]{1,32}$/;

interface Stored {
  v: string;
  t: number; // 写入时间戳（ms）
}

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function validRef(raw: string | null | undefined): string | null {
  const v = (raw ?? '').trim();
  return REF_RE.test(v) ? v : null;
}

function parse(raw: string | null): Stored | null {
  if (!raw) return null;
  try {
    const o: unknown = JSON.parse(raw);
    if (typeof o !== 'object' || o === null || !('v' in o) || !('t' in o)) return null;
    const { v, t } = o;
    if (typeof v !== 'string' || typeof t !== 'number' || !Number.isFinite(t)) return null;
    return validRef(v) === null ? null : { v, t };
  } catch {
    return null;
  }
}

function drop(ls: Storage): void {
  try {
    ls.removeItem(REF_KEY);
  } catch {
    // 忽略：删除失败下次读取仍按过期判定
  }
}

/** 记住推广码（新码覆盖旧码，有效期自 now 起算）。非法码不写。 */
export function rememberRef(raw: string | null | undefined, now: number): void {
  const v = validRef(raw);
  const ls = store();
  if (v === null || ls === null) return;
  try {
    ls.setItem(REF_KEY, JSON.stringify({ v, t: now } satisfies Stored));
  } catch (e) {
    console.warn('[gp:ref] 推广码留存失败（存储不可用），仅本次访问生效', e);
  }
}

/** 读回未过期的推广码；过期、损坏、未来时间戳（时钟回拨 / 篡改）一律删除并返回 null。 */
export function recallRef(now: number): string | null {
  const ls = store();
  if (ls === null) return null;
  let raw: string | null;
  try {
    raw = ls.getItem(REF_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const s = parse(raw);
  if (s === null || s.t > now || now - s.t > REF_TTL_MS) {
    drop(ls);
    return null;
  }
  return s.v;
}

/** 入口统一口径：URL / start_param 带合法新码 → 记住并返回；否则返回存储中未过期的旧码；都没有 → null。 */
export function takeRef(fromUrl: string | null | undefined, now: number): string | null {
  const v = validRef(fromUrl);
  if (v !== null) {
    rememberRef(v, now);
    return v;
  }
  return recallRef(now);
}
